import React, { useState, useEffect, useRef } from "react";
import { useAgencyData } from "@/hooks/useAgencyData";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

const AgencyWhitelabel: React.FC = () => {
  const { agency, loading, updateAgency, refetchAgency } = useAgencyData();
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingLogoLight, setUploadingLogoLight] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lightFileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    variant: 'dark' | 'light'
  ) => {
    const file = e.target.files?.[0];
    const inputRef = variant === 'dark' ? fileInputRef : lightFileInputRef;
    const setUploading = variant === 'dark' ? setUploadingLogo : setUploadingLogoLight;
    const field = variant === 'dark' ? 'logo_url' : 'logo_url_light';
    const fileName = variant === 'dark' ? 'logo' : 'logo-light';
    const successLabel = variant === 'dark' ? 'Logo Black' : 'Logo White';

    if (!file || !supabase || !agency) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato não suportado. Use PNG, JPG, SVG ou WebP.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 2MB.');
      return;
    }

    setUploading(true);
    try {
      const fileExt = (file.name.split('.').pop() || 'png').toLowerCase();
      const filePath = `${agency.id}/${fileName}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('agency-logos')
        .upload(filePath, file, { upsert: true, contentType: file.type });

      if (uploadError) {
        if (uploadError.message?.includes('Bucket not found')) {
          toast.error('Bucket de storage não configurado. Contate o administrador.');
          return;
        }
        throw uploadError;
      }

      const { data: urlData } = supabase.storage
        .from('agency-logos')
        .getPublicUrl(filePath);

      if (!urlData?.publicUrl) {
        throw new Error('Não foi possível obter a URL pública da logo');
      }

      // Bust browser/CDN cache when replacing the same path
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      setFormData(prev => ({ ...prev, [field]: publicUrl }));

      const saved = await updateAgency(
        variant === 'dark'
          ? { logo_url: publicUrl, updated_at: new Date().toISOString() }
          : { logo_url_light: publicUrl, updated_at: new Date().toISOString() }
      );

      if (!saved) {
        toast.error('Logo enviada, mas falhou ao salvar no perfil da agência.');
        return;
      }

      sessionStorage.removeItem('agency_cache');
      sessionStorage.removeItem('agency_cache_time');
      await refetchAgency();

      toast.success(`${successLabel} carregada com sucesso!`);
    } catch (err: any) {
      console.error('Erro ao fazer upload:', err);
      toast.error(err?.message || 'Erro ao fazer upload da logo.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleTestSmtp = async () => {
    if (!formData.smtp_host || !formData.smtp_email || !formData.smtp_port || !formData.smtp_password) {
      toast.error('Preencha todos os campos SMTP antes de testar.');
      return;
    }

    setTestingSmtp(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/test-smtp`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            smtp_host: formData.smtp_host,
            smtp_email: formData.smtp_email,
            smtp_port: parseInt(formData.smtp_port),
            smtp_ssl: formData.smtp_ssl === 'true',
            smtp_password: formData.smtp_password,
            test_email: formData.smtp_email,
          })
        }
      );

      const result = await response.json();
      if (result.success) {
        toast.success('E-mail de teste enviado com sucesso!');
      } else {
        toast.error(result.error || 'Erro ao enviar e-mail de teste.');
      }
    } catch (err) {
      toast.error('Erro ao testar SMTP. Verifique os dados e tente novamente.');
    } finally {
      setTestingSmtp(false);
    }
  };

  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    complement: "",
    city: "",
    state: "",
    domain: "",
    api_url: "",
    smtp_host: "",
    smtp_email: "",
    smtp_port: "",
    smtp_ssl: "true",
    smtp_password: "",
    logo_url: "",
    logo_url_light: "",
  });

  useEffect(() => {
    if (agency) {
      setFormData({
        company_name: agency.company_name || "",
        contact_name: agency.contact_name || "",
        email: agency.email || "",
        phone: agency.phone || "",
        address: agency.address || "",
        complement: agency.complement || "",
        city: agency.city || "",
        state: agency.state || "",
        domain: agency.domain || "",
        api_url: agency.api_url || "",
        smtp_host: agency.smtp_host || "",
        smtp_email: agency.smtp_email || "",
        smtp_port: agency.smtp_port?.toString() || "",
        smtp_ssl: agency.smtp_ssl ? "true" : "false",
        smtp_password: agency.smtp_password || "",
        logo_url: agency.logo_url || "",
        logo_url_light: agency.logo_url_light || "",
      });
    }
  }, [agency]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const dataToUpdate: any = {
        company_name: formData.company_name,
        contact_name: formData.contact_name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address || null,
        complement: formData.complement || null,
        city: formData.city || null,
        state: formData.state || null,
        domain: formData.domain,
        api_url: formData.api_url,
        smtp_host: formData.smtp_host || null,
        smtp_email: formData.smtp_email || null,
        smtp_port: formData.smtp_port ? parseInt(formData.smtp_port) : null,
        smtp_ssl: formData.smtp_ssl === "true",
        smtp_password: formData.smtp_password || null,
        logo_url: formData.logo_url || null,
        logo_url_light: formData.logo_url_light || null,
        updated_at: new Date().toISOString(),
      };

      const success = await updateAgency(dataToUpdate);

      if (success) {
        setMessage({ type: 'success', text: 'Dados salvos com sucesso!' });
      } else {
        setMessage({ type: 'error', text: 'Erro ao salvar dados' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Erro ao salvar dados' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin dark:border-[#3F3F46] dark:border-t-[#60A5FA]"></div>
          <p className="text-sm text-muted-foreground">Carregando dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-foreground mb-2">
          Configurações White Label
        </h3>
        <p className="text-muted-foreground text-sm">
          Configure a identidade visual e informações da agência
        </p>
      </div>

      {message && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-[#ECFDF5] border border-green-200 text-[#16A34A] dark:bg-green-500/10 dark:border-green-500/30 dark:text-[#22C55E]'
              : 'bg-[#FEF2F2] border border-red-200 text-[#DC2626] dark:bg-red-500/10 dark:border-red-500/30 dark:text-[#F87171]'
          }`}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="border border-border bg-card rounded-[20px] shadow-sm p-6">
          <h4 className="text-lg font-semibold text-foreground mb-6">
            Configurações
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-3">
              <Label htmlFor="company_name" className="text-sm font-medium text-foreground">
                Nome do Empresa
              </Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                placeholder="Nome"
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                required
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="contact_name" className="text-sm font-medium text-foreground">
                Contacto
              </Label>
              <Input
                id="contact_name"
                value={formData.contact_name}
                onChange={(e) => handleInputChange('contact_name', e.target.value)}
                placeholder="Contacto"
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                required
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="email" className="text-sm font-medium text-foreground">
                E-mail
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="E-mail"
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                required
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="phone" className="text-sm font-medium text-foreground">
                Telefone
              </Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="Telefone"
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                required
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="address" className="text-sm font-medium text-foreground">
                Endereço
              </Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Endereço"
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="complement" className="text-sm font-medium text-foreground">
                Complemento
              </Label>
              <Input
                id="complement"
                value={formData.complement}
                onChange={(e) => handleInputChange('complement', e.target.value)}
                placeholder="Complemento"
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="city" className="text-sm font-medium text-foreground">
                Cidade
              </Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="Cidade"
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="state" className="text-sm font-medium text-foreground">
                Estado
              </Label>
              <Input
                id="state"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                placeholder="Estado"
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>
          </div>
        </div>

        <div className="border border-border bg-card rounded-[20px] shadow-sm p-6">
          <h4 className="text-lg font-semibold text-foreground mb-6">
            White Label
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-3">
              <Label htmlFor="logo_url" className="text-sm font-medium text-foreground">
                Logo Black
              </Label>
              <p className="text-xs text-muted-foreground -mt-1">
                Usada no tema escuro
              </p>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={(e) => handleLogoUpload(e, 'dark')}
              />
              <div className="flex gap-3">
                <Button
                  type="button"
                  className="rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {uploadingLogo ? 'Enviando...' : 'Upload'}
                </Button>
                <Input
                  id="logo_url"
                  value={formData.logo_url}
                  onChange={(e) => handleInputChange('logo_url', e.target.value)}
                  placeholder="URL da logo black"
                  className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                />
              </div>
              {formData.logo_url && (
                <div className="mt-2 rounded-lg bg-[#18181B] p-3 inline-flex">
                  <img
                    src={formData.logo_url}
                    alt="Preview logo black"
                    className="max-h-[60px] object-contain rounded"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="logo_url_light" className="text-sm font-medium text-foreground">
                Logo White
              </Label>
              <p className="text-xs text-muted-foreground -mt-1">
                Usada no tema claro
              </p>
              <input
                type="file"
                ref={lightFileInputRef}
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={(e) => handleLogoUpload(e, 'light')}
              />
              <div className="flex gap-3">
                <Button
                  type="button"
                  className="rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
                  onClick={() => lightFileInputRef.current?.click()}
                  disabled={uploadingLogoLight}
                >
                  {uploadingLogoLight ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {uploadingLogoLight ? 'Enviando...' : 'Upload'}
                </Button>
                <Input
                  id="logo_url_light"
                  value={formData.logo_url_light}
                  onChange={(e) => handleInputChange('logo_url_light', e.target.value)}
                  placeholder="URL da logo white"
                  className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                />
              </div>
              {formData.logo_url_light && (
                <div className="mt-2 rounded-lg bg-[#F1F5F9] p-3 inline-flex">
                  <img
                    src={formData.logo_url_light}
                    alt="Preview logo white"
                    className="max-h-[60px] object-contain rounded"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="domain" className="text-sm font-medium text-foreground">
                Domínio
              </Label>
              <Input
                id="domain"
                value={formData.domain}
                onChange={(e) => handleInputChange('domain', e.target.value)}
                placeholder="Domínio"
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                required
              />
            </div>

            <div className="flex flex-col gap-3 md:col-span-2">
              <Label htmlFor="api_url" className="text-sm font-medium text-foreground">
                API
              </Label>
              <Input
                id="api_url"
                value={formData.api_url}
                onChange={(e) => handleInputChange('api_url', e.target.value)}
                placeholder="API"
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                required
              />
            </div>
          </div>
        </div>

        <div className="border border-border bg-card rounded-[20px] shadow-sm p-6">
          <h4 className="text-lg font-semibold text-foreground mb-6">SMTP</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-3">
              <Label htmlFor="smtp_host" className="text-sm font-medium text-foreground">
                Host
              </Label>
              <Input
                id="smtp_host"
                value={formData.smtp_host}
                onChange={(e) => handleInputChange('smtp_host', e.target.value)}
                placeholder="Host"
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="smtp_email" className="text-sm font-medium text-foreground">
                E-mail
              </Label>
              <Input
                id="smtp_email"
                type="email"
                value={formData.smtp_email}
                onChange={(e) => handleInputChange('smtp_email', e.target.value)}
                placeholder="E-mail"
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="smtp_port" className="text-sm font-medium text-foreground">
                Porta
              </Label>
              <Input
                id="smtp_port"
                type="number"
                value={formData.smtp_port}
                onChange={(e) => handleInputChange('smtp_port', e.target.value)}
                placeholder="Porta"
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="smtp_ssl" className="text-sm font-medium text-foreground">
                SSL
              </Label>
              <Select
                value={formData.smtp_ssl}
                onValueChange={(value) => handleInputChange('smtp_ssl', value)}
              >
                <SelectTrigger className="h-11 rounded-xl text-sm text-[#0F172A] border border-[#E5E7EB] bg-white px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA]">
                  <SelectValue placeholder="SSL" />
                </SelectTrigger>
                <SelectContent className="rounded-xl bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA]">
                  <SelectItem value="true">
                    Sim
                  </SelectItem>
                  <SelectItem value="false">
                    Não
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-3 md:col-span-2">
              <Label htmlFor="smtp_password" className="text-sm font-medium text-foreground">
                Senha
              </Label>
              <Input
                id="smtp_password"
                type="password"
                value={formData.smtp_password}
                onChange={(e) => handleInputChange('smtp_password', e.target.value)}
                placeholder="Senha"
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] px-3 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            className="border-[#2563EB] text-[#2563EB] hover:bg-[#EEF4FF] hover:text-[#1D4ED8] dark:border-[#60A5FA] dark:text-[#60A5FA] dark:hover:bg-blue-500/15 min-w-[140px]"
            disabled={testingSmtp || !formData.smtp_host || !formData.smtp_email}
            onClick={handleTestSmtp}
          >
            {testingSmtp ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Testando...
              </>
            ) : 'Testar SMTP'}
          </Button>
          <Button
            type="submit"
            className="rounded-xl bg-[#2563EB] text-white hover:bg-[#1D4ED8] min-w-[120px] transition-colors duration-200"
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AgencyWhitelabel;
